/**
 * RadarEventDetailPage.js
 * 상한가 레이더 — 이벤트 상세 (개발계획서 9.3)
 *
 * radar_events/{id} 문서 + snapshots(잔량 추이) + tracking(재료 지속성 D+1~D+5)
 * 서브컬렉션을 한 화면에 표시한다. "드릴다운만 서브컬렉션" 원칙(9.5) — 목록
 * 화면에서는 안 불러오는 무거운 시계열 데이터를 이 화면에서만 조회한다.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, ExternalLink } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height:100vh;background:#0a0a0f;color:#e2e8f0;
  font-family:'Inter','Pretendard',sans-serif;padding:20px;
`;
const TopBar = styled.div`
  display:flex;align-items:center;gap:12px;
  margin-bottom:24px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;
`;
const BackBtn = styled.button`
  background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;
  &:hover{background:#1e2030;color:#e2e8f0}
`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`font-size:22px;font-weight:800;margin:0;color:#e2e8f0;`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;

const Grid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px;
  margin-bottom:20px;animation:${fadeIn} .4s ease;
`;
const InfoCard = styled.div`background:#111827;border:1px solid #1e2030;border-radius:12px;padding:16px;`;
const InfoLabel = styled.p`margin:0 0 6px;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.05em;`;
const InfoVal = styled.div`font-size:18px;font-weight:700;color:#e2e8f0;`;

const Section = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:14px;padding:20px;
  margin-bottom:20px;animation:${fadeIn} .4s ease;
`;
const SectionTitle = styled.h3`margin:0 0 14px;font-size:14px;font-weight:700;color:#94a3b8;`;

const MaterialBadge = styled.span`
  display:inline-block;font-size:11px;font-weight:700;padding:3px 10px;border-radius:8px;
  background:rgba(139,92,246,0.12);color:#8b5cf6;margin-bottom:10px;
`;
const SummaryText = styled.p`margin:0 0 14px;font-size:14px;color:#e2e8f0;line-height:1.6;`;
const SourceLink = styled.a`
  display:flex;align-items:center;gap:6px;font-size:12px;color:#64748b;
  text-decoration:none;margin-bottom:6px;
  &:hover{color:#f97316}
`;

const D1Grid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(120px,1fr));gap:10px;
`;
const D1Cell = styled.div`text-align:center;padding:10px;background:#0d1117;border-radius:10px;`;
const D1Label = styled.p`margin:0 0 4px;font-size:10px;color:#64748b;`;
const D1Val = styled.p`
  margin:0;font-size:15px;font-weight:700;
  color:${p => p.$pos === true ? '#10b981' : p.$pos === false ? '#ef4444' : '#e2e8f0'};
`;

const TrackTable = styled.div`width:100%;font-size:12px;`;
const TrackRow = styled.div`
  display:grid;grid-template-columns:60px 90px 70px 70px 60px;gap:8px;
  padding:8px 4px;border-bottom:1px solid #1e2030;align-items:center;
`;
const ScoreBar = styled.div`
  height:6px;border-radius:3px;background:#1e2030;overflow:hidden;
  &::after{content:'';display:block;height:100%;width:${p => p.$score}%;
    background:${p => p.$score >= 60 ? '#10b981' : p.$score >= 30 ? '#f59e0b' : '#ef4444'};}
`;

const HistoryScroll = styled.div`overflow-x:auto;margin-top:16px;`;
const HistoryTable = styled.div`width:100%;font-size:12px;min-width:640px;`;
const HistoryRow = styled.div`
  display:grid;grid-template-columns:88px 84px 84px 84px 84px 72px 72px;gap:8px;
  padding:9px 4px;border-bottom:1px solid #1e2030;align-items:center;text-align:right;
  & > span:first-child{text-align:left;}
  &:last-child{border-bottom:none;}
`;

const Empty = styled.div`padding:40px;text-align:center;color:#475569;font-size:12px;`;
const Loading = styled.div`padding:60px;text-align:center;color:#64748b;font-size:13px;`;

const fmtHitTime = (event) => {
  if (event.hit_time_precision === 'daily_close_only' || !event.hit_time) return '종가 기준 (장중 시각 미상)';
  const ts = event.hit_time._seconds ? new Date(event.hit_time._seconds * 1000) : new Date(event.hit_time);
  return `약 ${ts.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}경`;
};
const fmtPct = (v) => (v == null ? '-' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);
const fmtSnapshotTime = (t) => {
  const ts = t?._seconds ? new Date(t._seconds * 1000) : new Date(t);
  return ts.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};
// YYYYMMDD -> YYYY-MM-DD
const fmtDateLabel = (d) => (d && d.length === 8 ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d || '-');
// d1_stats는 종가 대비 비율만 저장하므로, 절대가는 close_price × (1 + ratio)로 역산한다.
const priceFromRatio = (closePrice, ratio) => (closePrice != null && ratio != null ? Math.round(closePrice * (1 + ratio)) : null);

export default function RadarEventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  // 대시보드가 선택했던 날짜/필터(쿼리스트링)를 그대로 복원하려면 새 경로로
  // navigate하지 않고 브라우저 히스토리를 한 칸 되돌려야 한다 — 대시보드
  // 진입 없이 이 상세페이지로 바로 들어온 경우(북마크 등)를 대비해 히스토리
  // 스택이 없으면 기본 경로로 폴백한다.
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/labs/radar');
    }
  };

  const [event, setEvent] = useState(null);
  const [tickerStats, setTickerStats] = useState(null);
  const [tickerStatsLoading, setTickerStatsLoading] = useState(true);
  const [tickerEvents, setTickerEvents] = useState([]);
  const [tickerEventsLoading, setTickerEventsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setEvent(await apiService.getRadarEvent(eventId));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // 종목별 과거 상한가 이력(radar_ticker_stats)은 이벤트 문서에 없는 별도
  // 집계 데이터라 독립적으로 조회한다 — 아직 통계가 없는 종목(신규 상한가
  // 등)이면 404가 정상이므로 조용히 null 처리한다.
  useEffect(() => {
    if (!event?.ticker) return;
    setTickerStatsLoading(true);
    apiService.getRadarTickerStats(event.ticker)
      .then(setTickerStats)
      .catch(() => setTickerStats(null))
      .finally(() => setTickerStatsLoading(false));
  }, [event?.ticker]);

  // 종목의 개별 상한가 발생 이력(날짜별 상세) — 과거 상한가 이력 섹션의 리스트용
  useEffect(() => {
    if (!event?.ticker) return;
    setTickerEventsLoading(true);
    apiService.getRadarTickerEvents(event.ticker)
      .then(list => setTickerEvents(list.filter(e => !e.excluded_flag)))
      .catch(() => setTickerEvents([]))
      .finally(() => setTickerEventsLoading(false));
  }, [event?.ticker]);

  if (loading) return <Page><Loading>불러오는 중...</Loading></Page>;
  if (error || !event) {
    return (
      <Page>
        <TopBar>
          <BackBtn onClick={handleBack}><ArrowLeft size={18} /></BackBtn>
        </TopBar>
        <Empty>이벤트를 불러오지 못했습니다{error ? `: ${error}` : ''}</Empty>
      </Page>
    );
  }

  const reason = event.reason || {};
  const d1 = event.d1_stats;
  const snapshots = (event.snapshots || []).map(s => ({
    ...s,
    timeLabel: fmtSnapshotTime(s.time),
  }));
  const tracking = event.tracking || [];

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={handleBack}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>{event.name} ({event.ticker})</PageTitle>
          <PageSub>{event.market} · {event.date} · {fmtHitTime(event)}</PageSub>
        </TitleArea>
      </TopBar>

      <Grid>
        <InfoCard><InfoLabel>종가</InfoLabel><InfoVal>{event.close_price?.toLocaleString()}원</InfoVal></InfoCard>
        <InfoCard>
          <InfoLabel>등락률</InfoLabel>
          <InfoVal style={{ color: '#f97316' }}>{event.change_rate != null ? `+${event.change_rate.toFixed(2)}%` : '-'}</InfoVal>
        </InfoCard>
        <InfoCard><InfoLabel>연속 상한가</InfoLabel><InfoVal>{event.consecutive_days ?? '-'}일</InfoVal></InfoCard>
        <InfoCard><InfoLabel>거래량</InfoLabel><InfoVal>{event.volume?.toLocaleString() ?? '-'}</InfoVal></InfoCard>
        <InfoCard>
          <InfoLabel>매수잔량비율</InfoLabel>
          <InfoVal>{event.remain_ratio_vol != null ? `${(event.remain_ratio_vol * 100).toFixed(1)}%` : '-'}</InfoVal>
        </InfoCard>
      </Grid>

      <Section>
        <SectionTitle>상승 사유</SectionTitle>
        <MaterialBadge>{reason.material_type || '미확인'}</MaterialBadge>
        <SummaryText>{reason.summary_text || '사유 미확인 (수급성 추정)'}</SummaryText>
        {(reason.source_urls || []).map((url, i) => (
          <SourceLink key={i} href={url} target="_blank" rel="noreferrer">
            <ExternalLink size={12} /> 출처 {i + 1}
          </SourceLink>
        ))}
      </Section>

      <Section>
        <SectionTitle>D+1 결과</SectionTitle>
        {!d1 ? (
          <Empty>{event.d1_pending ? 'D+1 결과 대기 중 (다음 거래일 확정 예정)' : '데이터 없음'}</Empty>
        ) : (
          <>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
              color: d1.up ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 16,
            }}>
              {d1.up ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
              {fmtPct(d1.close_ratio)}
            </div>
            <D1Grid>
              <D1Cell><D1Label>시가갭</D1Label><D1Val $pos={d1.open_gap > 0}>{fmtPct(d1.open_gap)}</D1Val></D1Cell>
              <D1Cell><D1Label>고가</D1Label><D1Val $pos={d1.high_ratio > 0}>{fmtPct(d1.high_ratio)}</D1Val></D1Cell>
              <D1Cell><D1Label>저가</D1Label><D1Val $pos={d1.low_ratio > 0}>{fmtPct(d1.low_ratio)}</D1Val></D1Cell>
              <D1Cell><D1Label>종가</D1Label><D1Val $pos={d1.close_ratio > 0}>{fmtPct(d1.close_ratio)}</D1Val></D1Cell>
              <D1Cell><D1Label>캔들위치</D1Label><D1Val>{(d1.candle_position * 100).toFixed(0)}%</D1Val></D1Cell>
            </D1Grid>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>과거 상한가 이력 — {event.name} 종목 전체</SectionTitle>
        {tickerStatsLoading ? (
          <Empty>불러오는 중...</Empty>
        ) : !tickerStats ? (
          <Empty>이 종목의 과거 상한가 통계가 아직 없습니다(이번이 첫 상한가이거나, D+1 결과가 아직 확정되지 않은 이력만 있는 경우).</Empty>
        ) : (
          <>
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14,
            }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: '#e2e8f0' }}>
                {tickerStats.limit_up_count_10y}회
              </span>
              <span style={{ fontSize: 12, color: '#64748b' }}>최근 10년간 상한가 발생</span>
            </div>
            <D1Grid>
              <D1Cell>
                <D1Label>다음날 상승확률</D1Label>
                <D1Val $pos={tickerStats.d1_up_rate >= 0.5}>{(tickerStats.d1_up_rate * 100).toFixed(0)}%</D1Val>
              </D1Cell>
              <D1Cell><D1Label>평균 고가</D1Label><D1Val $pos={tickerStats.d1_high_avg > 0}>{fmtPct(tickerStats.d1_high_avg)}</D1Val></D1Cell>
              <D1Cell><D1Label>최고 고가</D1Label><D1Val $pos={tickerStats.d1_high_max > 0}>{fmtPct(tickerStats.d1_high_max)}</D1Val></D1Cell>
              <D1Cell><D1Label>평균 저가</D1Label><D1Val $pos={tickerStats.d1_low_avg > 0}>{fmtPct(tickerStats.d1_low_avg)}</D1Val></D1Cell>
              <D1Cell><D1Label>최저 저가</D1Label><D1Val $pos={tickerStats.d1_low_min > 0}>{fmtPct(tickerStats.d1_low_min)}</D1Val></D1Cell>
              <D1Cell><D1Label>평균 종가</D1Label><D1Val $pos={tickerStats.d1_close_avg > 0}>{fmtPct(tickerStats.d1_close_avg)}</D1Val></D1Cell>
            </D1Grid>
          </>
        )}

        {tickerEventsLoading ? (
          <Empty>이력 불러오는 중...</Empty>
        ) : tickerEvents.length === 0 ? (
          <Empty>과거 상한가 이력이 없습니다.</Empty>
        ) : (
          <HistoryScroll>
            <HistoryTable>
              <HistoryRow style={{ fontWeight: 700, color: '#64748b', fontSize: 10 }}>
                <span>상한가 시기</span>
                <span>상한가 가격</span>
                <span>D+1 종가</span>
                <span>D+1 최저가</span>
                <span>D+1 최고가</span>
                <span>최고 상승률</span>
                <span>최저 상승률</span>
              </HistoryRow>
              {tickerEvents.map(e => {
                const hd1 = e.d1_stats;
                return (
                  <HistoryRow key={e.id}>
                    <span>{fmtDateLabel(e.date)}{e.id === event.id && <span style={{ color: '#f97316' }}> ●</span>}</span>
                    <span>{e.close_price != null ? `${e.close_price.toLocaleString()}원` : '-'}</span>
                    {hd1 ? (
                      <>
                        <span>{priceFromRatio(e.close_price, hd1.close_ratio)?.toLocaleString()}원</span>
                        <span style={{ color: hd1.low_ratio >= 0 ? '#10b981' : '#ef4444' }}>
                          {priceFromRatio(e.close_price, hd1.low_ratio)?.toLocaleString()}원
                        </span>
                        <span style={{ color: hd1.high_ratio >= 0 ? '#10b981' : '#ef4444' }}>
                          {priceFromRatio(e.close_price, hd1.high_ratio)?.toLocaleString()}원
                        </span>
                        <span style={{ color: hd1.high_ratio >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                          {fmtPct(hd1.high_ratio)}
                        </span>
                        <span style={{ color: hd1.low_ratio >= 0 ? '#10b981' : '#ef4444', fontWeight: 700 }}>
                          {fmtPct(hd1.low_ratio)}
                        </span>
                      </>
                    ) : (
                      <span style={{ gridColumn: 'span 4', color: '#64748b', textAlign: 'center' }}>D+1 결과 대기 중</span>
                    )}
                  </HistoryRow>
                );
              })}
            </HistoryTable>
          </HistoryScroll>
        )}
      </Section>

      <Section>
        <SectionTitle>장중 잔량 추이 (10분 간격)</SectionTitle>
        {snapshots.length === 0 ? (
          <Empty>스냅샷 데이터가 없습니다 (과거 백필 이벤트는 실시간 폴링 데이터가 없습니다).</Empty>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={snapshots} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e2030" />
              <XAxis dataKey="timeLabel" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip contentStyle={{ background: '#111827', border: '1px solid #1e2030', borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="remain_qty" name="매수잔량" stroke="#f97316" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Section>

      <Section>
        <SectionTitle>재료 지속성 (D+1~D+5)</SectionTitle>
        {tracking.length === 0 ? (
          <Empty>아직 추적된 데이터가 없습니다.</Empty>
        ) : (
          <TrackTable>
            <TrackRow style={{ fontWeight: 700, color: '#64748b', fontSize: 10 }}>
              <span>일자</span><span>가격유지</span><span>후속뉴스</span><span>점수</span><span></span>
            </TrackRow>
            {tracking.map(t => (
              <TrackRow key={t.id}>
                <span>D+{t.day_offset}</span>
                <span style={{ color: t.price_ratio >= 1 ? '#10b981' : '#ef4444' }}>{fmtPct(t.price_ratio - 1)}</span>
                <span>{t.follow_up_news_count}건</span>
                <span style={{ fontWeight: 700 }}>{t.score}점</span>
                <ScoreBar $score={t.score} />
              </TrackRow>
            ))}
          </TrackTable>
        )}
      </Section>
    </Page>
  );
}
